import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';

import { ApplicationErrorModal } from 'components/modal/ApplicationErrorModal';
import { useClientContext } from 'context/ClientContext';
import { SessionState, useTokenRenewal } from 'hooks/useTokenRenewal';

export type KeycloakAuth = {
    keycloak: Keycloak;
    token: string | null;
    sessionState: SessionState;
    logout: () => void;
    login: () => void;
    updatePassword: () => void;
    stopTokenRenewal: () => void;
};

const INIT_OPTIONS: KeycloakInitOptions = {
    onLoad: 'check-sso', // check-sso verifica se o usuário já está autenticado sem forçar login
    pkceMethod: 'S256', // usa PKCE para segurança adicional
    checkLoginIframe: false, // desativado para evitar problemas com cookies de terceiros
    enableLogging: import.meta.env.DEV, // habilita logs para debug
};

export const AuthContext = createContext<KeycloakAuth>({} as KeycloakAuth);

export function AuthProvider({ children }: { children: ReactNode }) {
    const { clientId, name } = useClientContext();
    const [token, setToken] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const initializedRef = useRef(false);
    const keycloakRef = useRef<Keycloak | null>(null);

    if (!keycloakRef.current) {
        keycloakRef.current = new Keycloak({
            url: import.meta.env.VITE_KEYCLOAK_URL,
            realm: import.meta.env.VITE_KEYCLOAK_REALM,
            clientId: import.meta.env.DEV ? 'ciss-localhost' : clientId,
        });
    }

    const keycloak = keycloakRef.current;
    const { startTokenRenewal, stopTokenRenewal, sessionState } = useTokenRenewal(keycloak);

    /**
     * - limpa o cache do react query
     * - faz login no keycloak
     */
    const login = () => {
        queryClient.clear();

        keycloak.login({
            prompt: 'login',
            redirectUri: window.location.href,
            maxAge: 0,
        });
    };

    /**
     * - para o autoRenew do token
     * - limpa o cache do react query
     * - faz logout no keycloak
     */
    const logout = () => {
        queryClient.clear();

        keycloak.logout({ redirectUri: window.location.origin });
    };

    /**
     * - redireciona para a tela de atualização de senha
     */
    const updatePassword = () => {
        const url = keycloak.authServerUrl;
        const realm = keycloak.realm;
        const clientId = keycloak.clientId;
        const redirectUri = window.location.origin;

        // validarrrr
        location.replace(
            '',
            // `${url}realms/${realm}/protocol/openid-connect/auth?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&kc_action=UPDATE_PASSWORD`,
        );
    };

    /**
     * - inicializa o keycloak com as configurações iniciais.
     * - caso esteja validado, define o token e inicia o autoRenew do token.
     * - caso não esteja validado, redireciona para a tela de login.
     */
    const init = async () => {
        // evita init duplicado em modo strict no desenvolvimento
        if (initializedRef.current) return;

        initializedRef.current = true;

        const authenticated = await keycloak.init({ ...INIT_OPTIONS, scope: `organization:${name}` });

        if (authenticated) {
            // ao autenticar, define o token e inicia o autoRenew do token.
            setToken(keycloak.token as string);

            startTokenRenewal(setToken);
        } else {
            login();
        }
    };

    useEffect(() => {
        init();
    }, []);

    // enquanto não está autenticado, não renderiza nada a não ser o loader da aplicação.
    if (!token) return null;

    return (
        <AuthContext.Provider
            value={{
                keycloak,
                token,
                sessionState,
                login,
                logout,
                updatePassword,
                stopTokenRenewal,
            }}
        >
            {sessionState.status === 'expired' && <ApplicationErrorModal type="session" />}

            {children}
        </AuthContext.Provider>
    );
}

export function useAuthContext() {
    return useContext(AuthContext);
}
